import { createContext, ReactNode, useContext } from 'react';
import { useParams } from 'react-router';
import { Client } from 'src/users';
import { useClients } from '../hooks/useClients';

type CurrentClient = {
  clientId: string;
  client: Client | null;
  refetch: ReturnType<typeof useClients>['refetch'];
  isLoading: boolean;
  isClientPage: boolean;
};

type CurrentClientContextType = {
  clients: Client[];
  isLoading: boolean;
  refetch: ReturnType<typeof useClients>['refetch'];
};

const CurrentClientContext = createContext<CurrentClientContextType | undefined>(undefined);

type CurrentClientProviderProps = {
  children: ReactNode;
};

export function CurrentClientProvider({ children }: CurrentClientProviderProps) {
  const { clients, isLoading, refetch } = useClients();

  return (
    <CurrentClientContext
      value={{
        clients,
        isLoading,
        refetch,
      }}
    >
      {children}
    </CurrentClientContext>
  );
}

export function useCurrentClientIfAvailable() {
  const context = useContext(CurrentClientContext);
  if (context === undefined) {
    throw new Error('useCurrentClientIfAvailable must be used within a CurrentClientProvider');
  }

  const { clientId: clientIdParam } = useParams<{ clientId: string }>();
  const clientId = clientIdParam;
  const hasClientId = clientId != null && clientId !== 'undefined';
  const client = hasClientId ? (context.clients.find(c => c.id === clientId) ?? null) : null;

  return {
    ...context,
    isClientPage: clientIdParam != null,
    clientId,
    client,
  };
}

export function useCurrentClient(): CurrentClient {
  const data = useCurrentClientIfAvailable();

  if (hasClientId(data)) {
    return data as CurrentClient;
  }

  throw new Error('Client ID is null');
}

function hasClientId(data: Partial<CurrentClient>): data is CurrentClient {
  return data.clientId != null;
}
